import React from 'react';
import { App, FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';

import { useKeycloak } from '@App/settings/keycloak';
import { useErrorModal } from '@App/components/ErrorModalProvider';
import { handler } from '@App/settings/ApiHandler';
import KC from '@App/@types/keycloakTypes';
import { LdapUserInfo } from '../UserSearchModalPublic';
import {
  buildPermissionsPayload,
  buildRoleHolderPayload,
  updateProjectPermissions,
} from '../ProjectAccessCard';

type FormField = {
  name: string;
  required?: boolean;
  settings?: {
    required?: boolean;
    dependencies?: unknown;
  };
};

type ProjectFormMeta = {
  uuid?: string | number | null;
  slug?: string | number | null;
};

type UseProjectAccessSubmitProps = {
  /** Existing project uuid (required for the combined submit) */
  projectUuid?: string;
  /** Currently shown section: the form or the access block */
  onSectionChange: (section: 'form' | 'access') => void;
  /** Form definition - used to know which fields are required */
  fields?: FormField[];
  /** Already saved form values (used until the user edits the form) */
  prefillFormData?: Record<string, unknown>;
  /** Form meta sent with the form save */
  formMeta: ProjectFormMeta;
  sharedWithUsers: LdapUserInfo[];
  responsibleResearcher: LdapUserInfo | null;
  principalInvestigator: LdapUserInfo | null;
  /** Called after the form and the permissions were saved successfully */
  onSuccess: () => void;
};

const saveResearchProject = (kc: KC.KCType, uuid: string, payload: API.PayloadType) =>
  handler<Responses.Default>({
    method: 'PUT',
    path: `/v3/researchProjects/${uuid}`,
    payload,
  }, kc);

/**
 * Guided submission for research projects (used in the update modals):
 *
 * - the form tab's submit only validates and moves on to the access tab ("next"),
 * - the actual submit lives on the access tab and stays disabled until all
 *   required form fields are filled and a responsible researcher is picked,
 * - it saves the form data and the permissions together, so nothing is lost when
 *   only one of them was filled in.
 */
export const useProjectAccessSubmit = ({
  projectUuid,
  onSectionChange,
  fields,
  prefillFormData,
  formMeta,
  sharedWithUsers,
  responsibleResearcher,
  principalInvestigator,
  onSuccess,
}: UseProjectAccessSubmitProps) => {
  const { message } = App.useApp();
  const { showError } = useErrorModal();
  const { t } = useTranslation(['forms', 'local']);
  const { keycloak } = useKeycloak();

  const formRef = React.useRef<FormInstance | null>(null);
  const [liveFormValues, setLiveFormValues] = React.useState<Record<string, unknown> | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  // Readiness heuristic for the submit button: required fields without
  // dependencies must have a value (the authoritative check runs on submit)
  const requiredFormFields = (fields ?? []).filter(
    (field) => (field.required ?? field.settings?.required) && !field.settings?.dependencies
  );
  const currentFormValues = (liveFormValues ?? prefillFormData ?? {}) as Record<string, unknown>;
  const formValuesComplete = requiredFormFields.every((field) => {
    const value = currentFormValues[field.name];
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== '';
  });
  const canSubmitAll = Boolean(projectUuid) && formValuesComplete && Boolean(responsibleResearcher);

  // The form tab's submit (onFinish only fires when valid) -> next step
  const goToAccessTab = () => onSectionChange('access');

  const handleSubmitAll = async () => {
    if (!projectUuid || !keycloak) return;
    if (!responsibleResearcher) {
      showError(t('forms.error.responsibleResearcherRequired', 'Vastutav uurija on kohustuslik. Määra projektile vastutav uurija.'));
      return;
    }

    // Validate the current form state authoritatively
    let formValues: object | undefined;
    try {
      formValues = await formRef.current?.validateFields();
    } catch {
      showError(t('forms.error.missingFields', 'Palun täida kõik kohustuslikud väljad.'));
      onSectionChange('form');
      return;
    }

    setIsSubmitting(true);
    try {
      const formRes = await saveResearchProject(keycloak, projectUuid, {
        action: 'update' as API.ActionType,
        payload: {
          form_uuid: formMeta.uuid,
          form_slug: formMeta.slug,
          uuid: projectUuid,
          form_data: formValues,
          sharedWith: sharedWithUsers,
          responsibleResearcher: buildRoleHolderPayload(responsibleResearcher),
        },
      });
      if (formRes.error) {
        // Already shown by the ApiHandler - keep everything open so nothing entered is lost
        return;
      }

      const permRes = await updateProjectPermissions(keycloak, projectUuid, {
        action: 'update' as API.ActionType,
        payload: buildPermissionsPayload(sharedWithUsers, responsibleResearcher, principalInvestigator),
      });
      if (permRes.error) {
        return;
      }

      message.success(t('forms.success.projectSubmitted', 'Projekt edukalt esitatud'));
      onSuccess();
    } catch (err) {
      console.error('Failed to submit research project:', err);
      message.info(t('forms.error.tryAgain', 'Palun proovi uuesti'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formRef,
    setLiveFormValues,
    isSubmitting,
    canSubmitAll,
    goToAccessTab,
    handleSubmitAll,
  };
};

export default useProjectAccessSubmit;
